'use client'
import cn from 'classnames'
import { motion } from 'motion/react'
import Image from 'next/image'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import { Illustration, illustrationOf } from '@/components/Illustration'
import type { Content } from '@/content-types'
import { isFilled } from '@/lib/content-fields'
import { ContentLink, ContentNextImage, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { linkResolver } from '@/utils/linkResolver'

/**
 * Props for `Hero`.
 */
export type HeroProps = SliceComponentProps<Content.HeroSlice>

/**
 * Component for "Hero" Slices.
 */
const Hero: FC<HeroProps> = ({ slice }) => {
  return (
    <section data-slice-type={slice.slice_type} data-slice-variation={slice.variation}>
      {slice.variation === 'default' && slice.primary.dark_background && <DarkHero slice={slice} />}
      {slice.variation === 'default' && !slice.primary.dark_background && <DefaultHero slice={slice} />}
      {slice.variation === 'simple' && <SimpleHero slice={slice} />}
    </section>
  )
}

const DefaultHero = ({ slice }: { slice: Extract<HeroProps['slice'], { variation: 'default' }> }) => {
  const illustration = illustrationOf(slice.primary)
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-10%] -z-10 h-[520px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(129,12,168,0.14),transparent)] md:right-[5%]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 left-[-10%] -z-10 h-[420px] w-[420px] rounded-full bg-[radial-gradient(closest-side,rgba(193,71,233,0.10),transparent)]"
      />
      <div className="container flex flex-col-reverse items-center justify-between gap-y-8 md:flex-row md:gap-x-10 lg:gap-x-16">
        <div className="min-w-0 flex-1 space-y-5">
          <RichText
            field={slice.primary.title}
            components={{
              heading1: ({ children }) => (
                <h1 className="text-[44px]/[48px] font-light md:text-5xl/[52px] lg:text-[60px]/[62px]">
                  {children}
                </h1>
              ),
              strong: ({ children }) => <strong className="font-bold">{children}</strong>,
              heading2: ({ children }) => <h2 className="text-32 md:text-48">{children}</h2>,
              heading3: ({ children }) => <h3 className="text-24 md:text-40">{children}</h3>,
            }}
          />

          <RichText
            field={slice.primary.subtitle}
            components={{
              paragraph: ({ children }) => (
                <p className="gap-1.5 text-xl font-medium break-words">{children}</p>
              ),
              strong: ({ children }) => <strong className="text-primary mb-2">{children}</strong>,
              list: ({ children }) => <ul className="list-disc space-y-2">{children}</ul>,
              listItem: ({ children }) => (
                <li className="flex items-center gap-2 text-[19px] leading-7 text-[#2d033b]">
                  <IconComponent icon="CheckMark" size="xsmall" className="text-primary mt-1 shrink-0" />
                  <span>{children}</span>
                </li>
              ),
            }}
          />
          <RichText
            field={slice.primary.description}
            components={{
              heading5: ({ children }) => (
                <h5 className="[&_a]:text-primary text-xl leading-7 font-medium break-words text-[#2f2f2f] md:text-xl">
                  {children}
                </h5>
              ),
              paragraph: ({ children }) => (
                <p className="[&_a]:text-primary gap-1.5 text-lg leading-7 font-medium break-words text-[#2f2f2f] md:text-lg">
                  {children}
                </p>
              ),
              list: ({ children }) => <ul className="list-disc space-y-2">{children}</ul>,
              listItem: ({ children }) => (
                <li className="flex items-center gap-2 text-[19px] leading-7 text-[#2d033b]">
                  <IconComponent icon="CheckMark" size="xsmall" className="text-primary mt-1 shrink-0" />
                  <span>{children}</span>
                </li>
              ),
            }}
          />
          {slice.primary.buttons && slice.primary.buttons.length > 0 && (
            <div className="mt-8">
              <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center sm:gap-6">
                {slice.primary.buttons.map((button) => {
                  return (
                    <ContentLink
                      key={button.button.text}
                      field={button.button}
                      linkResolver={linkResolver}
                      className={cn(
                        button.button.variant === 'Contained'
                          ? 'bg-primary hover:bg-primary-dark rounded-full border-1 px-7 py-3 text-white'
                          : '',
                        button.button.variant === 'Text'
                          ? 'hover:text-primary flex items-center justify-center gap-2 text-[#2d033b]'
                          : '',
                        'group items-center text-center text-base font-semibold text-nowrap transition duration-200 ease-in-out xl:text-lg'
                      )}
                    >
                      {button.button.variant === 'Text' && (
                        <IconComponent
                          icon="ChevronRight"
                          size="xsmall"
                          className="group-hover:text-primary text-primary"
                        />
                      )}
                      {button.button.text}
                    </ContentLink>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {illustration ? (
          <div className={cn('w-full min-w-0 md:w-1/2 md:shrink lg:max-w-[626px]')}>
            <Illustration name={illustration} />
          </div>
        ) : (
          slice.primary.image?.url && (
            <div className={cn('w-full min-w-0 md:w-1/2 md:shrink lg:max-w-[626px] lg:self-stretch')}>
              <div className="relative h-full overflow-hidden rounded-3xl shadow-[0_30px_80px_-30px_rgba(45,3,59,0.45)] ring-1 ring-black/10">
                <ContentNextImage
                  field={slice.primary.image}
                  sizes="(min-width: 1024px) 626px, (min-width: 768px) 50vw, 100vw"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          )
        )}
      </div>
    </section>
  )
}

/**
 * Product-first hero on an ink background. Used when the slice's `dark_background`
 * flag is on (the homepage). The dashboard screenshot is the real redesigned portal;
 * the slice's own image (a customer installation) floats beside it.
 */
const DarkHero = ({ slice }: { slice: Extract<HeroProps['slice'], { variation: 'default' }> }) => {
  const eyebrow = (slice.primary as { eyebrow?: string }).eyebrow
  const reassurance =
    (slice.primary as { reassurance?: string }).reassurance ?? 'No credit card required. Cancel any time.'
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation="dark"
      className="bg-ink relative overflow-hidden pt-36 text-white md:pt-44"
    >
      <div aria-hidden="true" className="ink-grid pointer-events-none absolute inset-0" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-64 left-1/2 h-[720px] w-[1100px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(129,12,168,0.55),transparent)] blur-2xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-[520px] right-[-200px] h-[600px] w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(193,71,233,0.28),transparent)] blur-2xl"
      />

      {/* No opacity fade here or on the product frame: the h1 and the dashboard
          screenshot are the LCP candidates, and a fade holds them invisible until
          hydration plus the animation has run. A slide alone paints immediately. */}
      <motion.div
        initial={{ y: 18 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative container flex flex-col items-center text-center"
      >
        {eyebrow && (
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[13px] font-medium text-white/85 backdrop-blur">
            <span className="bg-signal size-1.5 rounded-full shadow-[0_0_12px_2px_rgba(193,71,233,0.8)]" />
            {eyebrow}
          </span>
        )}

        <RichText
          field={slice.primary.title}
          components={{
            heading1: ({ children }) => (
              <h1 className="max-w-[20ch] text-[40px]/[1.04] font-light tracking-tight md:text-[60px]/[1.02] lg:text-[72px]/[1.0]">
                {children}
              </h1>
            ),
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          }}
        />

        <RichText
          field={slice.primary.subtitle}
          components={{
            paragraph: ({ children }) => (
              <p className="mt-6 max-w-[42ch] text-lg text-white/75 md:text-[21px] md:leading-8">
                {children}
              </p>
            ),
          }}
        />

        {slice.primary.buttons && slice.primary.buttons.length > 0 && (
          <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
            {slice.primary.buttons.map((button) => (
              <ContentLink
                key={button.button.text}
                field={button.button}
                linkResolver={linkResolver}
                className={cn(
                  'inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[16px] font-semibold transition duration-200',
                  button.button.variant === 'Contained'
                    ? 'bg-primary hover:bg-signal text-white shadow-[0_12px_40px_-10px_rgba(193,71,233,0.7)]'
                    : 'border border-white/20 bg-white/5 text-white hover:bg-white/10'
                )}
              >
                {button.button.text}
                {button.button.variant !== 'Contained' && (
                  <IconComponent icon="ChevronRight" size="xsmall" className="text-signal" />
                )}
              </ContentLink>
            ))}
          </div>
        )}

        <p className="mt-5 text-[13px] text-white/50">{reassurance}</p>
      </motion.div>

      {/* Product frame */}
      <motion.div
        initial={{ y: 40 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative container mt-16 md:mt-20"
      >
        <div className="relative mx-auto max-w-[1100px]">
          <div className="rounded-[22px] bg-white/10 p-1.5 ring-1 ring-white/15 backdrop-blur-sm md:rounded-[28px] md:p-2">
            <div className="overflow-hidden rounded-[16px] bg-[#f4f2f5] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.7)] md:rounded-[22px]">
              <div className="flex h-9 items-center gap-1.5 border-b border-black/5 bg-white px-4">
                <span className="size-2.5 rounded-full bg-[#ff5f57]" />
                <span className="size-2.5 rounded-full bg-[#febc2e]" />
                <span className="size-2.5 rounded-full bg-[#28c840]" />
                <span
                  aria-hidden="true"
                  className="mx-auto hidden rounded-md bg-black/5 px-16 py-1 text-[11px] text-black/60 md:block"
                >
                  signital.app/portal
                </span>
              </div>
              <Image
                src="/images/portal/dashboard-home.png"
                alt="The Signital dashboard: fleet health, displays that need attention, and every location at a glance"
                width={2560}
                height={1440}
                // The frame sits inside the 24px container padding plus its own 6px
                // border, so below 1200px the image is 60px narrower than the viewport.
                sizes="(min-width: 1200px) 1100px, calc(100vw - 60px)"
                priority
                fetchPriority="high"
                className="block h-auto w-full"
              />
            </div>
          </div>

          {/* Floating: pairing dialog */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: 'easeOut' }}
            className="absolute -right-4 bottom-[-40px] hidden w-[300px] rotate-2 overflow-hidden rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/20 lg:block xl:-right-16"
          >
            <Image
              src="/images/portal/connect-dialog-code.png"
              alt="Connect a display by typing the 5-character pairing code"
              width={1280}
              height={936}
              sizes="300px"
              className="block h-auto w-full"
            />
          </motion.div>

          {/* Floating: real installation photo */}
          {slice.primary.image?.url && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: 'easeOut' }}
              className="absolute -left-4 bottom-[-56px] hidden w-[260px] -rotate-3 overflow-hidden rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/20 lg:block xl:-left-16"
            >
              <ContentNextImage
                field={slice.primary.image}
                sizes="260px"
                className="block aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-left">
                <p className="text-[11px] font-semibold tracking-wide text-white/70 uppercase">
                  Live at a customer
                </p>
                <p className="text-[13px] font-medium text-white">Smart panels, no media player</p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Fade into the next section */}
      <div className="h-24 bg-gradient-to-b from-transparent to-white md:h-32" />
    </section>
  )
}

const SimpleHero = ({ slice }: { slice: Extract<HeroProps['slice'], { variation: 'simple' }> }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="container mb-20 pt-36 text-center"
  >
    {isFilled.link(slice.primary.parent) && (
      <div className="mb-1">
        <ContentLink field={slice.primary.parent} linkResolver={linkResolver} className="text-small-header">
          {slice.primary.parent.text}
        </ContentLink>
      </div>
    )}

    <RichText
      field={slice.primary.title}
      components={{
        heading1: ({ children }) => <h1 className="text-[40px]/[1.1] md:text-6xl">{children}</h1>,
        heading2: ({ children }) => <h2 className="text-32 md:text-48">{children}</h2>,
        heading3: ({ children }) => <h3 className="text-24 md:text-40">{children}</h3>,
        strong: ({ children }) => <strong className="font-bold">{children}</strong>,
      }}
    />

    <hr className="bg-primary mx-auto my-7 h-[3px] w-full max-w-[70px]" />

    {isFilled.keyText(slice.primary.subtitle) && (
      <p className="text-primary mb-2 gap-1.5 text-xl md:text-[22px]">{slice.primary.subtitle}</p>
    )}

    <RichText
      field={slice.primary.description}
      components={{
        heading5: ({ children }) => (
          <h5 className="[&_a]:text-primary text-xl font-medium break-words text-[#2f2f2f] md:text-xl">
            {children}
          </h5>
        ),
        paragraph: ({ children }) => (
          <p className="[&_a]:text-primary text-small-header font-light">{children}</p>
        ),
        list: ({ children }) => <ul className="list-disc space-y-2">{children}</ul>,
        listItem: ({ children }) => (
          <li className="flex items-center gap-2 text-[19px] text-[#2d033b]">
            <IconComponent icon="CheckMark" size="xsmall" className="text-primary mt-1 shrink-0" />
            <span>{children}</span>
          </li>
        ),
      }}
    />
  </section>
)

export default Hero
